<template>
    <v-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)" max-width="800"
        :fullscreen="isMobile" persistent @keydown.esc="chiudi">
        <v-card v-if="manutenzione">
            <v-card-title class="d-flex align-center">
                <v-icon color="success" class="mr-2">mdi-wrench-check</v-icon>
                <div>
                    <div>Completa manutenzione</div>
                    <div class="text-caption text-medium-emphasis font-weight-regular">
                        {{ manutenzione.macchinaNome }} — {{ manutenzione.tipoManutenzioneNome }}
                    </div>
                </div>
            </v-card-title>

            <v-card-text>
                <v-alert v-if="erroreStock" type="error" variant="tonal" density="comfortable" class="rounded-lg mb-3"
                    closable @click:close="erroreStock = null">
                    {{ erroreStock }}
                </v-alert>

                <v-stepper v-model="step" flat>
                    <v-stepper-header>
                        <v-stepper-item title="Ricambi" value="1" />
                        <v-divider />
                        <v-stepper-item title="Foto e note" value="2" />
                        <v-divider />
                        <v-stepper-item title="Conferma" value="3" />
                    </v-stepper-header>

                    <div class="stepper-scroll-area">
                        <v-stepper-window>
                            <!-- Step 1: ricambi usati -->
                            <v-stepper-window-item value="1">
                                <v-alert v-if="ricambiPrevistiOriginali.length" type="info" variant="tonal"
                                    density="compact" class="rounded-lg mb-3">
                                    Precompilato dai materiali previsti in pianificazione: correggi le quantità
                                    effettivamente utilizzate.
                                </v-alert>

                                <div v-if="ricambiUsati.length === 0" class="text-body-2 text-medium-emphasis mb-2">
                                    Nessun ricambio utilizzato.
                                </div>

                                <div v-for="(riga, idx) in ricambiUsati" :key="idx" class="d-flex align-center mb-2">
                                    <v-select v-model="riga.ricambioId" :items="ricambiDisponibili" item-title="nome"
                                        item-value="id" label="Ricambio" density="compact" variant="outlined"
                                        hide-details class="mr-2" />
                                    <v-text-field v-model.number="riga.quantita" type="number" label="Qtà"
                                        density="compact" variant="outlined" hide-details style="max-width: 100px"
                                        class="mr-2" />
                                    <v-btn icon variant="text" size="small" color="error" @click="rimuoviRiga(idx)">
                                        <v-icon>mdi-delete</v-icon>
                                    </v-btn>
                                </div>
                                <v-btn variant="outlined" size="small" prepend-icon="mdi-plus" @click="aggiungiRiga">
                                    Aggiungi ricambio
                                </v-btn>
                            </v-stepper-window-item>

                            <!-- Step 2: foto + note -->
                            <v-stepper-window-item value="2">
                                <div class="text-subtitle-2 font-weight-medium mb-2">Foto fine lavoro</div>
                                <v-file-input v-model="fileSelezionato" label="Carica foto" accept="image/*"
                                    density="compact" variant="outlined" prepend-icon="mdi-camera"
                                    @update:model-value="caricaFoto" />
                                <v-row v-if="manutenzione.foto.length" dense>
                                    <v-col v-for="f in manutenzione.foto" :key="f.id" cols="6" sm="4">
                                        <v-card variant="outlined" class="rounded-lg pa-2 d-flex align-center">
                                            <v-icon size="small" class="mr-1">mdi-image</v-icon>
                                            <span class="text-caption text-truncate">{{ f.nomeOriginale }}</span>
                                        </v-card>
                                    </v-col>
                                </v-row>

                                <v-divider class="my-3" />

                                <v-textarea v-model="note" label="Note di completamento" rows="3" variant="outlined" />
                            </v-stepper-window-item>

                            <!-- Step 3: conferma -->
                            <v-stepper-window-item value="3">
                                <v-card variant="outlined" class="rounded-lg pa-3 mb-3">
                                    <div class="text-caption text-medium-emphasis">Macchina</div>
                                    <div class="font-weight-medium mb-2">{{ manutenzione.macchinaNome }}</div>

                                    <div class="text-caption text-medium-emphasis">Tipo manutenzione</div>
                                    <div class="font-weight-medium mb-2">{{ manutenzione.tipoManutenzioneNome }}</div>

                                    <template v-if="ricambiValidi.length">
                                        <div class="text-caption text-medium-emphasis">Ricambi utilizzati</div>
                                        <div v-for="riga in ricambiValidi" :key="riga.ricambioId" class="text-body-2">
                                            {{ nomeRicambio(riga.ricambioId) }} — {{ riga.quantita }}
                                        </div>
                                    </template>

                                    <template v-if="note">
                                        <div class="text-caption text-medium-emphasis mt-2">Note</div>
                                        <div class="text-body-2">{{ note }}</div>
                                    </template>
                                </v-card>

                                <v-alert v-if="manutenzione.ricorrente" type="info" variant="tonal"
                                    density="comfortable" class="rounded-lg">
                                    <div class="font-weight-medium">Pianificazione ricorrente</div>
                                    <div class="text-body-2">
                                        Verrà generata una nuova occorrenza in fondo alla serie, mantenendo pieno
                                        l'orizzonte di anni configurato per l'azienda.
                                    </div>
                                </v-alert>
                                <v-alert v-else-if="prossimaDataPreview" type="info" variant="tonal"
                                    density="comfortable" class="rounded-lg">
                                    <div class="font-weight-medium">Prossima manutenzione pianificata</div>
                                    <div class="text-body-2">
                                        Frequenza del tipo "{{ manutenzione.tipoManutenzioneNome }}": prossima scadenza
                                        prevista <strong>{{ formattaData(prossimaDataPreview) }}</strong>.
                                    </div>
                                </v-alert>
                                <div v-else class="text-caption text-medium-emphasis">
                                    Questo tipo di manutenzione non ha una frequenza configurata: non verrà generata
                                    automaticamente una prossima occorrenza.
                                </div>
                            </v-stepper-window-item>
                        </v-stepper-window>
                    </div>
                </v-stepper>
            </v-card-text>

            <v-card-actions>
                <v-spacer />
                <v-btn variant="text" @click="chiudi">Annulla</v-btn>
                <v-btn v-if="step !== '1'" variant="text" @click="step = String(Number(step) - 1)">Indietro</v-btn>
                <v-btn v-if="step !== '3'" color="primary" @click="step = String(Number(step) + 1)">Avanti</v-btn>
                <v-btn v-else color="success" :loading="salvataggio" @click="completa">Completa manutenzione</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { upload_allegato_manutenzione } from "@/api/api.allegati.js";

export default {
    name: "DialogCompletaManutenzione",

    props: {
        modelValue: { type: Boolean, default: false },
        manutenzioneId: { type: [Number, String], default: null },
        macchinaId: { type: [Number, String], required: true },
    },
    emits: ["update:modelValue", "completata"],

    data() {
        return {
            step: "1",
            salvataggio: false,
            ricambiUsati: [],
            ricambiPrevistiOriginali: [],
            note: "",
            fileSelezionato: null,
            erroreStock: null,
        };
    },

    computed: {
        isMobile() {
            return this.$vuetify.display.smAndDown;
        },
        manutenzione() {
            return this.$store.getters["manutenzioni/corrente"];
        },
        ricambiDisponibili() {
            return this.$store.getters["ricambi/lista"];
        },
        ricambiValidi() {
            return this.ricambiUsati.filter(r => r.ricambioId && r.quantita > 0);
        },
        tipoCorrente() {
            if (!this.manutenzione) return null;
            const lista = this.$store.getters["tipiManutenzione/lista"];
            return lista.find(t => t.id === this.manutenzione.tipoManutenzioneId) || null;
        },
        prossimaDataPreview() {
            if (!this.tipoCorrente) return null;
            return this.calcolaProssimaData(
                new Date().toISOString().substr(0, 10),
                this.tipoCorrente.frequenzaValore,
                this.tipoCorrente.frequenzaUnita
            );
        },
    },

    watch: {
        async modelValue(val) {
            if (val && this.manutenzioneId) {
                this.step = "1";
                this.note = "";
                this.fileSelezionato = null;
                this.erroreStock = null;
                this.$store.dispatch("ricambi/carica");
                this.$store.dispatch("tipiManutenzione/carica");
                await this.$store.dispatch("manutenzioni/caricaDettaglio", this.manutenzioneId);

                const previsti = (this.manutenzione && this.manutenzione.ricambiPrevisti) || [];
                this.ricambiPrevistiOriginali = previsti;
                this.ricambiUsati = previsti.length
                    ? previsti.map(r => ({ ricambioId: r.ricambioId, quantita: r.quantita }))
                    : [];
            }
        },
    },

    methods: {
        aggiungiRiga() {
            this.ricambiUsati.push({ ricambioId: null, quantita: 1 });
        },

        rimuoviRiga(idx) {
            this.ricambiUsati.splice(idx, 1);
        },

        nomeRicambio(id) {
            const r = this.ricambiDisponibili.find(x => x.id === id);
            return r ? r.nome : "";
        },

        formattaData(iso) {
            if (!iso) return "";
            return new Date(iso + "T00:00:00").toLocaleDateString("it-IT", {
                day: "numeric", month: "long", year: "numeric",
            });
        },

        // Stessa logica di anteprima usata in DialogNuovaManutenzione (mirror di
        // AutomazioneScadenze.CalcolaProssimaData lato backend, confrontando per
        // nome dell'enum poiché il backend lo serializza come stringa), qui
        // applicata a partire da "oggi" perché il completamento chiude la
        // manutenzione ora.
        calcolaProssimaData(dataIso, frequenzaValore, frequenzaUnita) {
            if (!frequenzaValore || !frequenzaUnita) return null;
            const d = new Date(dataIso + "T00:00:00");
            switch (frequenzaUnita) {
                case "Giorni": d.setDate(d.getDate() + frequenzaValore); break;
                case "Settimane": d.setDate(d.getDate() + frequenzaValore * 7); break;
                case "Mesi": d.setMonth(d.getMonth() + frequenzaValore); break;
                case "Anni": d.setFullYear(d.getFullYear() + frequenzaValore); break;
                default: return null;
            }
            return this.toIsoLocale(d);
        },

        // Evita toISOString(): convertendo in UTC sposterebbe la data indietro di
        // un giorno nei fusi orari positivi (es. Italia) vicino alla mezzanotte.
        toIsoLocale(d) {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const g = String(d.getDate()).padStart(2, "0");
            return `${y}-${m}-${g}`;
        },

        async caricaFoto(file) {
            if (!file) return;
            try {
                await upload_allegato_manutenzione(this.manutenzioneId, file);
                await this.$store.dispatch("manutenzioni/caricaDettaglio", this.manutenzioneId);
                this.fileSelezionato = null;
            } catch (error) {
                console.log(error);
            }
        },

        chiudi() {
            this.$emit("update:modelValue", false);
        },

        async completa() {
            this.salvataggio = true;
            this.erroreStock = null;
            try {
                await this.$store.dispatch("manutenzioni/completa", {
                    id: this.manutenzioneId,
                    macchinaId: this.macchinaId,
                    dati: {
                        noteCompletamento: this.note,
                        ricambiUsati: this.ricambiValidi,
                    },
                });
                this.$emit("completata");
                this.chiudi();
            } catch (error) {
                console.log(error);
                this.erroreStock = (error.response && error.response.data && error.response.data.message)
                    || "Non è stato possibile completare la manutenzione. Riprova.";
                this.step = "1";
            }
            this.salvataggio = false;
        },
    },
};
</script>

<style scoped>
.stepper-scroll-area {
    height: 460px;
    overflow-y: auto;
}
</style>
